import { useMemo, useState } from 'react'
import { format, isAfter } from 'date-fns'
import { ColumnFiltersState, createColumnHelper, SortingState } from '@tanstack/react-table'

import { getNameInitials } from '@/helpers/getNameInitials'
import { Code, FilterType, UpdatedLog, RouteSelectionMode, UpdatedRecordsType } from '@/types/logs'
import { CommonHeader, SortHeader, filterByTimeRange, Table, TableHeader, useTable } from '@/ui/Table'
import { useGetUpdatedLogs } from '../../api/useGetUpdatedLogs'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { groupUpdatedRecords } from '../../helpers/groupUpdatedRecords'
import { Loader } from '@/ui/Loader'
import { UPDATED_RECORDS_LABEL_MAP } from '../../constant'
import { filterAndSortUpdatedRecords } from '../../helpers/filterAndSortUpdatedRecords'
import { filterLogsBy } from '@/modules/CombinedLogs/helpers/filterLogsBy'

import styles from './UpdatedRecordsTable.module.scss'

import { UpdatedRecordRow } from './UpdatedRecordRow'
import { DefaultValue } from './DefaultValue'
import { Location } from './Location'

import ChatIcon from '../../assets/chat.svg'
import { sortLogsBy } from '@/modules/CombinedLogs/helpers/sortLogsBy'
import { formatRouteFilterValues } from '@/modules/CombinedLogs/helpers/formatRouteFilterValues'
import { formatRunFilterValues } from '@/modules/CombinedLogs/helpers/formatRunFilterValues'
import { formatDutyFilterValues } from '@/modules/CombinedLogs/helpers/formatDutyFilterValues'
import { formatFleetFilterValues } from '@/modules/CombinedLogs/helpers/formatFleetFilterValues'
import { TripValue } from './TripValue'
import { TimestampValue } from './TimestampValue'
import {
    checkIsFixMechanicalInfoMissing,
    checkIsFixOfficialReportMissing,
} from '@/pages/UnfinishedRecords/helpers/checkRecords'
import { OfficialReportStatus } from '@/types/officialReport'
import CheckmarkIcon from '@/modules/CombinedLogs/assets/checkmark-icon.svg'
import { formatCodeFilterValues } from '@/modules/CombinedLogs/helpers/formatCodeFilterValues'
import { useGetCodes } from '@/modules/CombinedLogs/api/useGetCodes'
import { AuthorTooltip } from '@/modules/CombinedLogs/ui/components/TableForm/tooltips/AuthorTooltip'
import FlagIcon from '../../assets/flag.svg'
import { FlaggedCell } from '@/modules/CombinedLogs/ui/components/information/FlaggedCell'

const columnHelper = createColumnHelper<UpdatedLog>()
const columns = (codes: Code[]) => [
    columnHelper.accessor('code', {
        id: COMBINED_LOGS_COLUMNS.Code,
        header: (context) => (
            <CommonHeader
                label="Code"
                buttonLabel="Sort by code"
                searchPlaceholder="Type to search for code"
                {...context}
            />
        ),
        cell: (info) => info.getValue() || 'Code',
        meta: {
            alignCenter: true,
            width: '7.142rem',
            formatFilterValues: (values) => formatCodeFilterValues(values, FilterType.All, codes),
        },
        enableSorting: true,
    }),
    columnHelper.accessor('reference', {
        id: COMBINED_LOGS_COLUMNS.Reference,
        header: 'Reference',
        cell: (info) => info.getValue(),
        enableSorting: false,
        meta: {
            alignCenter: true,
            width: '7.142rem',
        },
    }),
    columnHelper.accessor((row) => row.routes, {
        id: COMBINED_LOGS_COLUMNS.Routes,
        header: (context) => (
            <CommonHeader
                {...context}
                buttonLabel="Sort by routes"
                searchPlaceholder="Type to search for routes"
                label="Routes"
            />
        ),
        cell: (info) => <DefaultValue {...info} />,
        meta: {
            alignCenter: true,
            width: '7.142rem',
            formatFilterValues: (values, table) => formatRouteFilterValues(values, table),
        },
        filterFn: filterLogsBy((log) => log.routeSelectionMode === RouteSelectionMode.None),
    }),
    columnHelper.accessor('trip', {
        id: COMBINED_LOGS_COLUMNS.Trip,
        header: (context) => (
            <CommonHeader
                {...context}
                buttonLabel="Sort by trip"
                searchPlaceholder="Type to search for trip"
                label="Trip"
            />
        ),
        cell: (info) => <TripValue {...info} />,
        meta: {
            alignCenter: true,
        },
    }),
    columnHelper.accessor('run', {
        id: COMBINED_LOGS_COLUMNS.Run,
        header: (context) => (
            <CommonHeader
                {...context}
                buttonLabel="Sort by run"
                searchPlaceholder="Type to search for run"
                label="Run"
            />
        ),
        cell: (info) => <DefaultValue {...info} />,
        sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Run, (log) => !log.run),
        filterFn: filterLogsBy((log) => !!log.run),
        meta: {
            alignCenter: true,
            formatFilterValues: (values, table) => formatRunFilterValues(values, table),
        },
    }),
    columnHelper.accessor('duty', {
        id: COMBINED_LOGS_COLUMNS.Duty,
        header: (context) => (
            <CommonHeader
                {...context}
                buttonLabel="Sort by duty"
                searchPlaceholder="Type to search for duty"
                label="Duty"
            />
        ),
        cell: (info) => <DefaultValue {...info} />,
        filterFn: filterLogsBy((log) => !log.duty.length),
        sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Duty, (log) => !log.duty?.length),
        meta: {
            alignCenter: true,
            formatFilterValues: (values, table) => formatDutyFilterValues(values, table),
        },
    }),
    columnHelper.accessor('fleet', {
        id: COMBINED_LOGS_COLUMNS.Fleet,
        header: (context) => (
            <CommonHeader
                {...context}
                buttonLabel="Sort by fleet"
                searchPlaceholder="Type to search for fleet"
                label="Fleet"
            />
        ),
        cell: (info) => <DefaultValue {...info} />,
        filterFn: filterLogsBy((log) => !log.fleet?.length),
        sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Fleet, (log) => !log.fleet?.length),
        meta: {
            alignCenter: true,
            formatFilterValues: (values, table) => formatFleetFilterValues(values, table),
        },
    }),
    columnHelper.accessor(
        (row) => ({
            locationFrom: row.locationFrom,
            locationTo: row.locationTo,
            locationType: row.locationType,
            direction: row.direction,
        }),
        {
            id: COMBINED_LOGS_COLUMNS.Location,
            header: 'Location',
            cell: (info) => <Location log={info.row.original} />,
            meta: {
                width: '26.142rem',
            },
        }
    ),
    columnHelper.accessor((row) => row.descriptionInfo.description, {
        id: COMBINED_LOGS_COLUMNS.Description,
        header: 'Description',
        cell: (info) => <DefaultValue {...info} />,
        meta: {
            width: '26.142rem',
        },
    }),
    columnHelper.accessor('delayInMinutes', {
        id: COMBINED_LOGS_COLUMNS.Delay,
        header: 'Delay',
        cell: (info) => {
            const value = info.getValue()

            if (!value) {
                return '-'
            }

            return <span>{`${value} ${!isNaN(value) ? 'min' : ''}`}</span>
        },
        meta: {
            alignCenter: true,
        },
    }),
    columnHelper.accessor('mileage', {
        id: COMBINED_LOGS_COLUMNS.Milage,
        header: 'Mileage',
        cell: (info) => <DefaultValue {...info} />,
        meta: {
            alignCenter: true,
        },
    }),
    columnHelper.accessor('author', {
        id: COMBINED_LOGS_COLUMNS.Author,
        header: 'Author',
        cell: (info) => (
            <AuthorTooltip author={info.getValue() || '-'}>
                <span className={styles.greyCell}>{info.getValue() ? getNameInitials(info.getValue()) : '-'}</span>
            </AuthorTooltip>
        ),
        meta: {
            alignCenter: true,
        },
    }),

    columnHelper.accessor('timestamp', {
        id: COMBINED_LOGS_COLUMNS.Timestamp,
        header: (context) => <SortHeader {...context} buttonLabel="Sort by incident time" label="Incident Time" />,
        cell: (info) => <TimestampValue {...info} />,
        filterFn: filterByTimeRange,
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
            return isAfter(rowB.original.timestamp, rowA.original.timestamp)
                ? 1
                : isAfter(rowA.original.timestamp, rowB.original.timestamp)
                  ? -1
                  : 0
        },
        meta: {
            alignCenter: true,
            width: '8rem',
        },
    }),
    columnHelper.accessor('createdAt', {
        id: 'updated',
        header: 'Updated',
        cell: (info) => {
            const createdAt = info.getValue() as string

            if (!createdAt) return <span className={styles.secondaryValue}>-</span>

            return (
                <span className={styles.secondaryValue}>{format(info.row.original.createdAt, 'HH:mm dd/MM/yyyy')}</span>
            )
        },
        meta: {
            alignCenter: true,
            width: '8rem',
        },
    }),
    columnHelper.display({
        id: COMBINED_LOGS_COLUMNS.Flag,
        header: () => (
            <span className={styles.iconHeader}>
                <FlagIcon />
            </span>
        ),
        cell: ({ row }) => <FlaggedCell log={row.original} />,
        meta: {
            alignCenter: true,
            width: '5rem',
        },
    }),
    columnHelper.display({
        id: COMBINED_LOGS_COLUMNS.Actions,
        cell: (info) => {
            if (!info.row.original.unfinishedType) {
                if (checkIsFixMechanicalInfoMissing(info.row.original)) {
                    return (
                        <div className={styles.mechanicalInfo}>
                            <CheckmarkIcon /> {UPDATED_RECORDS_LABEL_MAP[UpdatedRecordsType.ViewInfo]}
                        </div>
                    )
                }
                if (checkIsFixOfficialReportMissing(info.row.original)) {
                    const isReportSent = info.row.original.report?.status === OfficialReportStatus.Sent
                    return (
                        <div className={styles.mechanicalInfo}>
                            <CheckmarkIcon />{' '}
                            {
                                UPDATED_RECORDS_LABEL_MAP[
                                    isReportSent ? UpdatedRecordsType.ReportSent : UpdatedRecordsType.ReportUpdated
                                ]
                            }
                        </div>
                    )
                }
                return <></>
            }

            return (
                <div className={styles.updatedTypeContainer}>
                    {UPDATED_RECORDS_LABEL_MAP[info.row.original.unfinishedType]}
                    {info.row.original.unfinishedType === UpdatedRecordsType.Flagged && <ChatIcon />}
                </div>
            )
        },
        meta: {
            width: '12.714rem',
        },
    }),
]

export const UpdatedRecordsTable = () => {
    const { data: codes = [] } = useGetCodes()
    const { data: logs = [], isLoading } = useGetUpdatedLogs()

    const [sortingState, setSortingState] = useState<SortingState>([])
    const [filteringState, setFilteringState] = useState<ColumnFiltersState>([])

    const groupedLogs = useMemo(() => groupUpdatedRecords(logs), [logs])

    const filteredAndSortedLogs = useMemo(
        () => filterAndSortUpdatedRecords(groupedLogs, sortingState, filteringState),
        [groupedLogs, sortingState, filteringState]
    )
    const table = useTable<UpdatedLog>({
        data: groupedLogs,
        columns: columns(codes),
        manualFiltering: true,
        manualSorting: true,
        state: {
            sorting: sortingState,
            columnFilters: filteringState,
        },
        onSortingChange: setSortingState,
        onColumnFiltersChange: setFilteringState,
    })

    const formattedRows = useMemo(() => {
        const { filteredIds, sortedIds } = filteredAndSortedLogs
        const filteredRows = table.getCoreRowModel().rows.filter((row) => filteredIds.includes(row.original.id))

        return filteredRows.sort(
            (rowA, rowB) => sortedIds.indexOf(rowA.original.id) - sortedIds.indexOf(rowB.original.id)
        )
    }, [filteredAndSortedLogs])

    if (isLoading) {
        return <Loader />
    }

    return (
        <div className={styles.root}>
            <div className={styles.tableContainer}>
                <Table className={styles.table}>
                    <TableHeader<UpdatedLog> table={table} />
                    <tbody className={styles.tableBody}>
                        {formattedRows.map((row) => {
                            return <UpdatedRecordRow key={row.index} row={row} table={table} />
                        })}
                    </tbody>
                </Table>
            </div>
        </div>
    )
}
